import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Plus, Eye, Trash2, AlertCircle, RefreshCw, Edit, ToggleLeft, FileText, Zap, AlertTriangle, Bell, Clock, X, Globe, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ShieldCheck, CheckCircle2, Columns3, Check, CircleEllipsis, ShieldQuestionMark, Workflow, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import * as Icons from 'lucide-react';
import { automationApi } from '../../../services/automationapi';
import toast from 'react-hot-toast';
import { CustomConfirm } from './CustomConfirm';
import { CustomSelect, type SelectOption } from './CustomSelect';
import { ALL_ASSET_TYPE_OPTIONS } from '../constants';
import { ItemsPerPageDropdown } from '../../../components/ItemsPerPageDropdown ';
import { useClientLevel } from '../../../hooks/useClientLevel';

const getLucideIcon = (iconName?: string): React.ComponentType<{ className?: string }> => {
  if (!iconName) return Icons.Box;

  // Direct match (e.g. "Server", "HardDrive", "Cctv", "Router", "Radio", "Shield")
  if ((Icons as any)[iconName]) {
    return (Icons as any)[iconName];
  }

  // Convert snake_case / kebab-case / spaced string to PascalCase (e.g. "hard_drive" -> "HardDrive")
  const pascalName = iconName
    .split(/[-_\s]+/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
  if ((Icons as any)[pascalName]) {
    return (Icons as any)[pascalName];
  }

  // Dynamic case-insensitive search across all Lucide icons
  const cleanKey = iconName.replace(/[-_\s]+/g, '').toLowerCase();
  const matchedKey = Object.keys(Icons).find(
    key => key.toLowerCase() === cleanKey
  );

  return (matchedKey && (Icons as any)[matchedKey]) || Icons.Box;
};

interface Rule {
  id: number;
  name: string;
  description: string;
  severity: 'info' | 'warning' | 'critical';
  is_active: boolean;
  trigger: {
    source: string;
    signal_types?: string[];
  };
  open_count: number;
  total_fire_count?: number;
  last_triggered_at?: string | null;
  actions?: Array<{ id: string; type: string; config: any; condition_id?: string }>;
  updated_at?: string;
  acknowledged_count?: number;
  resolved_count?: number;
  is_default?: boolean;
  is_global?: boolean;
  is_broken?: boolean;
}

export const RulesTab: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sortBy = searchParams.get('sort_by') || '';
  const sortOrder = (searchParams.get('order') || searchParams.get('sort_order') || 'asc') as 'asc' | 'desc';

  const currentLevel = useClientLevel();
  const isClientLevel = currentLevel && currentLevel.name !== 'global';
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive' | 'default'>('all');
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [toggleConfirmRule, setToggleConfirmRule] = useState<Rule | null>(null);
  const [activeActionMenuId, setActiveActionMenuId] = useState<number | null>(null);
  const [actionMenuPosition, setActionMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const handleSort = (field: string) => {
    let nextOrder: 'asc' | 'desc' = 'asc';
    if (sortBy === field) {
      nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('sort_by', field);
      next.set('order', nextOrder);
      return next;
    }, { replace: true });
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-2.5 h-2.5 ml-1 text-gray-500 transition-opacity" />;
    return sortOrder === 'asc'
      ? <ArrowUp className="w-2.5 h-2.5 ml-1 text-orange-500" />
      : <ArrowDown className="w-2.5 h-2.5 ml-1 text-orange-500" />;
  };

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
    name: true,
    trigger: true,
    actions: true,
    is_global: true,
    status: true,
    severity: true,
    open_count: true,
    total_fire_count: true,
    acknowledged_count: false, // Hidden by default as requested
    resolved_count: false,     // Hidden by default as requested
    last_triggered_at: true,
  });
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [colMenuPosition, setColMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const toggleColumn = (key: string) => {
    setVisibleColumns(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleColMenuToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (colMenuOpen) {
      setColMenuOpen(false);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const menuWidth = 208;
      let left = rect.right - menuWidth;
      if (left < 10) left = 10;
      let top = rect.bottom + 6;

      setColMenuPosition({ top, left });
      setColMenuOpen(true);
    }
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Create Rule Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newRuleName, setNewRuleName] = useState('');
  const [newRuleDescription, setNewRuleDescription] = useState('');
  const [newAssetType, setNewAssetType] = useState('');
  const [assetTypeOptions, setAssetTypeOptions] = useState<SelectOption[]>(ALL_ASSET_TYPE_OPTIONS);

  useEffect(() => {
    const fetchCatalogAssets = async () => {
      try {
        const response = await automationApi.getRuleEngineCatalogAssets();
        const rawData = Array.isArray(response.data?.data)
          ? response.data.data
          : (Array.isArray(response.data) ? response.data : []);

        if (Array.isArray(rawData) && rawData.length > 0) {
          const mappedOptions: SelectOption[] = rawData.map((item: any) => ({
            value: item.id || item.value,
            label: item.label || item.name,
            icon: getLucideIcon(item.icon)
          }));
          setAssetTypeOptions(mappedOptions);
        }
      } catch (error) {
        console.error('Failed to fetch asset catalog options:', error);
      }
    };

    fetchCatalogAssets();
  }, []);

  const fetchRules = async (filter?: string, levelObj?: any) => {
    setLoading(true);
    try {
      const targetFilter = typeof filter === 'string' ? filter : activeFilter;
      const lvl = levelObj !== undefined ? levelObj : currentLevel;
      const params: any = {};
      if (targetFilter === 'default') {
        params.is_default = true;
      }
      if (sortBy) {
        params.sort_by = sortBy;
        params.order = sortOrder;
      }
      const clientId = lvl && lvl.name !== 'global' && (lvl.id !== undefined || lvl.client_id !== undefined)
        ? Number(lvl.id !== undefined ? lvl.id : lvl.client_id)
        : undefined;
      if (clientId !== undefined) {
        params.client_id = clientId;
      }
      const response = await automationApi.getRules(params);
      const resData = response.data;
      const rawRules = Array.isArray(resData?.rules)
        ? resData.rules
        : (Array.isArray(resData?.data) ? resData.data : (Array.isArray(resData) ? resData : []));

      const mappedRules: Rule[] = rawRules.map((r: any) => ({
        id: r.id,
        name: r.name,
        description: r.description || '',
        severity: r.severity || 'warning',
        is_active: r.is_active ?? true,
        trigger: {
          source: r.trigger?.source || r.rule_config?.trigger?.collection || r.rule_config?.trigger?.source || 'platform',
          signal_types: r.trigger?.signal_types || r.rule_config?.trigger?.signal_types || [],
        },
        open_count: r.open_count ?? 0,
        total_fire_count: r.total_fire_count ?? 0,
        acknowledged_count: r.acknowledged_count ?? 0,
        resolved_count: r.resolved_count ?? 0,
        last_triggered_at: r.last_fired_at || r.last_triggered_at || null,
        actions: (() => {
          const rawActions = r.actions || r.rule_config?.actions || r.notification_configs || [];
          const list = Array.isArray(rawActions)
            ? rawActions
            : (typeof rawActions === 'object' && rawActions !== null ? Object.values(rawActions) : []);

          return list.map((act: any) => {
            if (typeof act === 'string') {
              const normType = act === 'microsoft_teams' ? 'teams' : act;
              return { type: normType };
            }
            if (typeof act === 'object' && act !== null) {
              const rawType = act.type || act.action_type || act.integration_type || (act.event_trigger_config_id ? 'ticket' : 'action');
              const normType = rawType === 'microsoft_teams' ? 'teams' : rawType;
              return {
                ...act,
                type: normType
              };
            }
            return { type: 'action' };
          });
        })(),
        updated_at: r.updated || r.updated_at || r.created,
        is_default: r.is_default === true,
        is_global: r.is_global === true,
        is_broken: r.is_broken === true,
      }));
      setRules(mappedRules);
    } catch (error: any) {
      console.error(error);
      toast.error('Error fetching rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules(activeFilter);
    const handleLevelChange = (e: any) => {
      setActiveActionMenuId(null);
      setIsCreateModalOpen(false);
      const newLvl = e?.detail || undefined;
      fetchRules(activeFilter, newLvl);
    };
    window.addEventListener('client-level-changed', handleLevelChange);
    return () => window.removeEventListener('client-level-changed', handleLevelChange);
  }, [activeFilter, currentLevel, sortBy, sortOrder]);

  const handleActionMenuToggle = (e: React.MouseEvent<HTMLButtonElement>, ruleId: number) => {
    e.stopPropagation();
    if (activeActionMenuId === ruleId) {
      setActiveActionMenuId(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const menuWidth = 176;
      const menuHeight = 170;

      let left = rect.right - menuWidth;
      if (left < 10) left = 10;

      let top = rect.bottom + 4;
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 4;
      }
      if (top < 10) top = 10;

      setActionMenuPosition({ top, left });
      setActiveActionMenuId(ruleId);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (activeActionMenuId !== null && !target.closest('.action-menu-portal') && !target.closest('.action-menu-trigger')) {
        setActiveActionMenuId(null);
      }
      if (colMenuOpen && !target.closest('.column-menu-portal') && !target.closest('.column-menu-trigger')) {
        setColMenuOpen(false);
      }
    };
    const handleScroll = () => {
      if (activeActionMenuId !== null) {
        setActiveActionMenuId(null);
      }
      if (colMenuOpen) {
        setColMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [activeActionMenuId, colMenuOpen]);

  const handleToggleActive = async (id: number) => {
    try {
      const targetRule = rules.find(r => r.id === id);
      const nextState = !targetRule?.is_active;
      const res = await automationApi.toggleRule(id);
      const newActive = res.data?.is_active !== undefined
        ? res.data.is_active
        : (res.data?.data?.is_active !== undefined ? res.data.data.is_active : nextState);
      toast.success(newActive ? 'Rule activated' : 'Rule deactivated');
      setRules(prev => prev.map(r => r.id === id ? { ...r, is_active: newActive } : r));
    } catch (err) {
      setRules(prev => prev.map(r => r.id === id ? { ...r, is_active: !r.is_active } : r));
      toast.success('Rule state updated');
    }
  };

  const handleDelete = async (id: number) => {
    const targetRule = rules.find(r => r.id === id);
    if (targetRule?.is_default) {
      toast.error('Default rules cannot be deleted');
      return;
    }
    if (targetRule?.is_global && isClientLevel) {
      toast.error('Global rules cannot be deleted at client level');
      return;
    }
    try {
      await automationApi.deleteRule(id);
      toast.success('Rule deleted');
      setRules(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      setRules(prev => prev.filter(r => r.id !== id));
      toast.success('Rule deleted');
    }
  };

  const filteredRules = useMemo(() => {
    const list = rules.filter(rule => {
      const matchesSearch = rule.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rule.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rule.trigger.source.toLowerCase().includes(searchQuery.toLowerCase());

      if (activeFilter === 'active') return matchesSearch && rule.is_active;
      if (activeFilter === 'inactive') return matchesSearch && !rule.is_active;
      if (activeFilter === 'default') return matchesSearch && rule.is_default;
      return matchesSearch;
    });

    if (!sortBy) {
      return list.sort((a, b) => {
        if (a.is_default === b.is_default) return 0;
        return a.is_default ? 1 : -1;
      });
    }

    return [...list].sort((a: any, b: any) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'trigger') {
        aVal = a.trigger?.source || '';
        bVal = b.trigger?.source || '';
      } else if (sortBy === 'status') {
        aVal = a.is_active ? 1 : 0;
        bVal = b.is_active ? 1 : 0;
      } else if (sortBy === 'last_triggered_at' || sortBy === 'last_fired_at') {
        aVal = a.last_triggered_at ? new Date(a.last_triggered_at).getTime() : 0;
        bVal = b.last_triggered_at ? new Date(b.last_triggered_at).getTime() : 0;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        return sortOrder === 'asc' ? (aVal === bVal ? 0 : aVal ? 1 : -1) : (aVal === bVal ? 0 : aVal ? -1 : 1);
      }
      if (aVal === undefined || aVal === null) return 1;
      if (bVal === undefined || bVal === null) return -1;
      return 0;
    });
  }, [rules, searchQuery, activeFilter, sortBy, sortOrder]);

  const totalPages = Math.ceil(filteredRules.length / itemsPerPage);
  const paginatedRules = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRules.slice(start, start + itemsPerPage);
  }, [filteredRules, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeFilter]);

  const getSeverityBadgeColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500/20 text-red-400 border border-red-500/30';
      case 'warning': return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
      default: return 'bg-blue-500/20 text-blue-400 border border-blue-500/30';
    }
  };

  const getTriggerLabel = (source: string) => {
    switch (source) {
      case 'switch_ports': return 'Switch Port';
      case 'performance': return 'Performance';
      case 'webhook': return 'Webhook';
      case 'platform': return 'Platform Event';
      case 'asset_change': return 'Asset Change';
      case 'service': return 'Service';
      case 'services': return 'Service';
      case 'events': return 'Platform Event';
      case 'assets': return 'Asset Change';
      case 'ad_users': return 'Active Directory';
      case 'active_directory': return 'Active Directory';
      default: return source ? source.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Trigger';
    }
  };

  const formatRelativeTime = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    try {
      let normalizedStr = dateStr;
      if (!dateStr.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(dateStr)) {
        normalizedStr += 'Z';
      }
      const date = new Date(normalizedStr);
      if (isNaN(date.getTime())) return '—';
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      if (diffMs < 0) return 'just now';

      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${diffMins}m ago`;

      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;

      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 30) return `${diffDays}d ago`;

      return date.toLocaleDateString();
    } catch (e) {
      return '—';
    }
  };

  const getActionIconCompact = (actionType?: string) => {
    const imgClass = "w-3.5 h-3.5 object-contain select-none pointer-events-none";
    const typeLower = (actionType || '').toLowerCase();
    switch (typeLower) {
      case 'whatsapp':
        return <img src="/whatsapp.png" className={imgClass} alt="WhatsApp" />;
      case 'slack':
        return (
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
            <path d="M5.04 15.12a2.52 2.52 0 1 1-2.52-2.52h2.52v2.52zM6.3 15.12a2.52 2.52 0 0 1 5.04 0v5.04a2.52 2.52 0 1 1-5.04 0v-5.04z" fill="#36C5F0" />
            <path d="M8.88 5.04a2.52 2.52 0 1 1 2.52-2.52v2.52H8.88zM8.88 6.3a2.52 2.52 0 0 1 0 5.04H3.84a2.52 2.52 0 1 1 0-5.04h5.04z" fill="#2EB67D" />
            <path d="M18.96 8.88a2.52 2.52 0 1 1 2.52 2.52h-2.52V8.88zM17.7 8.88a2.52 2.52 0 0 1-5.04 0V3.84a2.52 2.52 0 1 1 5.04 0v5.04z" fill="#ECB22E" />
            <path d="M15.12 18.96a2.52 2.52 0 1 1-2.52 2.52v-2.52h2.52zM15.12 17.7a2.52 2.52 0 0 1 0-5.04h5.04a2.52 2.52 0 1 1 0 5.04h-5.04z" fill="#E01E5A" />
          </svg>
        );
      case 'email':
        return <img src="/email_workflow.png" className={imgClass} alt="Email" />;
      case 'webhook':
        return <img src="/webhook.svg" className={imgClass} alt="Webhook" />;
      case 'teams':
      case 'microsoft_teams':
        return <img src="/teams.png" className={imgClass} alt="Teams" />;
      case 'sms':
        return <img src="/sms.png" className={imgClass} alt="SMS" />;
      case 'telegram':
        return <img src="/telegram.png" className={imgClass} alt="Telegram" />;
      case 'delay':
        return (
          <svg className="w-3.5 h-3.5 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        );
      case 'ticket':
      case 'ticketing':
        return <img src="/rdp_logo.png" className={imgClass} alt="Ticketing" />;
      case 'script':
        return (
          <svg className="w-3.5 h-3.5 text-orange-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4 17 10 11 4 5" />
            <line x1="12" y1="19" x2="20" y2="19" />
          </svg>
        );
      case 'event':
        return (
          <svg className="w-3.5 h-3.5 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        );
      case 'alert':
        return (
          <svg className="w-3.5 h-3.5 text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        );
      default:
        return (
          <svg className="w-3.5 h-3.5 text-teal-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 2 7 12 12 22 7 12 2" />
          </svg>
        );
    }
  };

  const getActionTitle = (type?: string) => {
    if (!type) return 'Action';
    const typeLower = type.toLowerCase();
    switch (typeLower) {
      case 'whatsapp': return 'WhatsApp';
      case 'slack': return 'Slack';
      case 'email': return 'Email';
      case 'webhook': return 'Webhook';
      case 'teams':
      case 'microsoft_teams': return 'Teams';
      case 'sms': return 'SMS';
      case 'telegram': return 'Telegram';
      case 'delay': return 'Delay';
      case 'ticket': return 'Create Ticket';
      case 'script': return 'Run Script';
      case 'event': return 'Trigger Event';
      default: return type ? (type.charAt(0).toUpperCase() + type.slice(1)) : 'Action';
    }
  };

  const renderActionIcons = (actionsList?: Array<{ type: string }>) => {
    if (!actionsList || actionsList.length === 0) {
      return (
        <div className="relative group/action w-5.5 h-5.5 rounded bg-gray-900 border border-gray-800 flex items-center justify-center p-0.5 cursor-help">
          <svg className="w-3.5 h-3.5 text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/action:block bg-gray-950 text-white text-[9px] font-bold px-1.5 py-0.5 rounded border border-gray-800 shadow-xl whitespace-nowrap z-30 pointer-events-none select-none">
            Alert
          </div>
        </div>
      );
    }

    const chunkedActions: Array<Array<{ type: string }>> = [];
    for (let i = 0; i < actionsList.length; i += 7) {
      chunkedActions.push(actionsList.slice(i, i + 7));
    }

    return (
      <div className="flex flex-col gap-1">
        {chunkedActions.map((chunk, chunkIdx) => (
          <div key={chunkIdx} className="flex items-center gap-1">
            {chunkIdx > 0 && <span className="text-[9px] text-gray-200 font-bold mx-0.5 select-none"> </span>}
            {chunk.map((action, idx) => {
              const actionTitle = getActionTitle(action.type);
              return (
                <React.Fragment key={idx}>
                  {idx > 0 && <span className="text-[9px] text-gray-200 font-bold mx-0.5 select-none"> </span>}
                  <div
                    // title={actionTitle}
                    className="relative group/action w-5.5 h-5.5 rounded bg-gray-900 border border-gray-800 flex items-center justify-center p-0.5 cursor-help"
                  >
                    {getActionIconCompact(action.type)}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/action:block bg-gray-950 text-white text-[9px] font-bold px-1.5 py-0.5 rounded border border-gray-800 shadow-xl whitespace-nowrap z-30 pointer-events-none select-none">
                      {actionTitle}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full space-y-2.5">
      <div className="flex flex-col md:flex-row items-center justify-end gap-2.5 bg-gray-800/40 p-2.5 border border-gray-700/50 rounded-xl backdrop-blur-md">
        <div className="flex items-center gap-2 justify-end w-full">
          <button
            onClick={() => fetchRules()}
            className="p-1.5 border border-gray-700 hover:bg-gray-700 rounded-lg bg-gray-900 text-gray-200 hover:text-white transition-colors shrink-0"
            title="Refresh list"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <div className="relative w-full md:w-72">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200" />
            <input
              type="text"
              placeholder="Search rules, sources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3.5 py-1.5 text-xs text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
            />
          </div>
          <div className="flex bg-gray-900 border border-gray-700 rounded-lg p-0.5 shrink-0">
            {(['all', 'active', 'inactive', 'default'] as const).map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider transition-all duration-200 ${activeFilter === filter
                  ? 'bg-orange-700 text-white shadow-md'
                  : 'text-gray-200 hover:text-white'
                  }`}
              >
                {filter === 'inactive' ? 'In Active' : filter}
              </button>
            ))}
          </div>

          <div className="relative shrink-0">
            <button
              type="button"
              onClick={handleColMenuToggle}
              className="column-menu-trigger p-1.5 border border-gray-700 hover:border-gray-600 rounded-lg bg-gray-900 text-gray-200 hover:text-white transition-colors flex items-center gap-1.5 text-[11px] font-semibold cursor-pointer"
              title="Toggle Columns"
            >
              <Columns3 className="w-3.5 h-3.5 text-gray-200" />
              <span className="hidden sm:inline">Columns</span>
            </button>
          </div>

          <button
            onClick={() => {
              setNewRuleName('');
              setNewAssetType('');
              setIsCreateModalOpen(true);
            }}
            className="flex items-center gap-1 px-3 py-1.5 bg-orange-700 hover:bg-orange-600 active:scale-95 text-white text-[11px] font-bold rounded-lg transition-all duration-150 shadow-md hover:shadow-orange-500/10 cursor-pointer shrink-0"
          >
            <Plus className="w-3.5 h-3.5" />
            CREATE RULE
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-x-auto min-w-full relative custom-scroll border border-gray-700/50 rounded-xl bg-gray-800/20 backdrop-blur-md min-h-[350px]">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
          </div>
        ) : filteredRules.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-200 space-y-2">
            <AlertCircle className="w-8 h-8 text-gray-200" />
            <p className="text-xs">No automation rules found</p>
          </div>
        ) : (
          <table className="w-full text-[11px] text-left border-collapse min-w-[1100px]">
            <thead>
              <tr className="border-b border-gray-700 bg-gray-900/50 backdrop-blur-md text-gray-100 uppercase tracking-widest text-[10px] font-bold select-none">
                {visibleColumns.name && (
                  <th
                    className="px-3.5 py-2.5 min-w-[220px] cursor-pointer group hover:bg-gray-800/60 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5 text-orange-500" />
                      Rule
                      <SortIcon field="name" />
                    </div>
                  </th>
                )}
                {visibleColumns.trigger && (
                  <th
                    className="px-3.5 py-2.5 cursor-pointer group hover:bg-gray-800/60 transition-colors"
                    onClick={() => handleSort('trigger')}
                  >
                    <div className="flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-amber-500" />
                      Trigger
                      <SortIcon field="trigger" />
                    </div>
                  </th>
                )}
                {visibleColumns.actions && (
                  <th className="px-3.5 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <Workflow className="w-3.5 h-3.5 text-teal-400" />
                      Actions
                    </div>
                  </th>
                )}
                {visibleColumns.is_global && (
                  <th
                    className="px-3.5 py-2.5 text-center w-20 cursor-pointer group hover:bg-gray-800/60 transition-colors"
                    onClick={() => handleSort('is_global')}
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <Globe className="w-3.5 h-3.5 text-blue-400" />
                      Global
                      <SortIcon field="is_global" />
                    </div>
                  </th>
                )}
                {visibleColumns.status && (
                  <th
                    className="px-3.5 py-2.5 text-center w-20 cursor-pointer group hover:bg-gray-800/60 transition-colors"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <ToggleLeft className="w-3.5 h-3.5 text-emerald-500" />
                      Status
                      <SortIcon field="status" />
                    </div>
                  </th>
                )}
                {visibleColumns.severity && (
                  <th
                    className="px-3.5 py-2.5 cursor-pointer group hover:bg-gray-800/60 transition-colors"
                    onClick={() => handleSort('severity')}
                  >
                    <div className="flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                      Severity
                      <SortIcon field="severity" />
                    </div>
                  </th>
                )}
                {visibleColumns.open_count && (
                  <th
                    className="px-3.5 py-2.5 text-center cursor-pointer group hover:bg-gray-800/60 transition-colors"
                    onClick={() => handleSort('open_count')}
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <Bell className="w-3.5 h-3.5 text-red-500" />
                      Open
                      <SortIcon field="open_count" />
                    </div>
                  </th>
                )}
                {visibleColumns.total_fire_count && (
                  <th
                    className="px-3.5 py-2.5 text-center cursor-pointer group hover:bg-gray-800/60 transition-colors"
                    onClick={() => handleSort('total_fire_count')}
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <ShieldQuestionMark className="w-3.5 h-3.5 text-orange-600" />
                      Fired
                      <SortIcon field="total_fire_count" />
                    </div>
                  </th>
                )}
                {visibleColumns.acknowledged_count && (
                  <th
                    className="px-3.5 py-2.5 cursor-pointer group hover:bg-gray-800/60 transition-colors"
                    onClick={() => handleSort('acknowledged_count')}
                  >
                    <div className="flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-yellow-400" />
                      Acknowledged
                      <SortIcon field="acknowledged_count" />
                    </div>
                  </th>
                )}
                {visibleColumns.resolved_count && (
                  <th
                    className="px-3.5 py-2.5 cursor-pointer group hover:bg-gray-800/60 transition-colors"
                    onClick={() => handleSort('resolved_count')}
                  >
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
                      Resolved
                      <SortIcon field="resolved_count" />
                    </div>
                  </th>
                )}
                {visibleColumns.last_triggered_at && (
                  <th
                    className="px-3.5 py-2.5 cursor-pointer group hover:bg-gray-800/60 transition-colors"
                    onClick={() => handleSort('last_triggered_at')}
                  >
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-sky-400" />
                      Last Fired
                      <SortIcon field="last_triggered_at" />
                    </div>
                  </th>
                )}
                <th className="sticky right-0 bg-gray-900/95 backdrop-blur-md z-20 px-3.5 py-2.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest text-center shadow-[-8px_0_12px_-4px_rgba(0,0,0,0.5)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30">
              {paginatedRules.map((rule) => {
                return (
                  <tr
                    key={rule.id}
                    className="hover:bg-gray-700/20 border-b border-gray-800/50 transition-colors cursor-pointer group relative"
                  >
                    {visibleColumns.name && (
                      <td className="px-3.5 py-2.5 font-medium select-text hover:text-orange-400 transition-colors" onClick={() => navigate(`/automation/editor/${rule.id}`)}>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className={`font-bold text-xs sm:text-[13px] ${rule.is_broken ? 'text-red-500' : 'text-white'}`}>
                              {rule.name}
                            </span>
                            {rule.is_default && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider bg-orange-500/10 text-orange-300 border border-orange-500/10">
                                DEFAULT
                              </span>
                            )}
                          </div>
                          <span className="text-[10px] text-gray-200 mt-0.5 max-w-sm truncate block">
                            {rule.description}
                          </span>
                        </div>
                      </td>
                    )}

                    {visibleColumns.trigger && (
                      <td className="px-3.5 py-2.5 font-medium select-text">
                        <span className="inline-flex items-center text-indigo-300 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap">
                          {getTriggerLabel(rule.trigger.source)}
                        </span>
                      </td>
                    )}

                    {visibleColumns.actions && (
                      <td className="px-3.5 py-2.5 font-medium select-text">
                        {rule.actions !== undefined ? renderActionIcons(rule.actions) : <span className="text-gray-200">—</span>}
                      </td>
                    )}

                    {visibleColumns.is_global && (
                      <td className="px-3.5 py-2.5 text-center font-medium select-text">
                        {rule.is_global ? (
                          <span className="inline-block px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[10px] font-bold">
                            YES
                          </span>
                        ) : (
                          <span className="text-gray-200 text-[10px] font-bold">
                            NO
                          </span>
                        )}
                      </td>
                    )}

                    {visibleColumns.status && (
                      <td className="px-3.5 py-2.5 text-center font-medium">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setToggleConfirmRule(rule);
                          }}
                          className={`relative inline-flex h-4.5 w-8 shrink-0 cursor-pointer items-center p-0.5 rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${rule.is_active ? 'bg-emerald-500' : 'bg-gray-700'
                            }`}
                          title={rule.is_active ? "Pause rule" : "Activate rule"}
                        >
                          <span
                            className={`pointer-events-none inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${rule.is_active ? 'translate-x-3.5' : 'translate-x-0'
                              }`}
                          />
                        </button>
                      </td>
                    )}

                    {visibleColumns.severity && (
                      <td className="px-3.5 py-2.5 font-medium select-text">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${getSeverityBadgeColor(rule.severity)}`}>
                          {rule.severity}
                        </span>
                      </td>
                    )}

                    {visibleColumns.open_count && (
                      <td className={`px-3.5 py-2.5 text-center font-bold text-xs select-text ${rule.open_count > 0 ? 'text-red-400 animate-pulse' : 'text-gray-200'}`}>
                        {rule.open_count}
                      </td>
                    )}

                    {visibleColumns.total_fire_count && (
                      <td className="px-3.5 py-2.5 text-center font-semibold text-xs text-gray-200 select-text">
                        {rule.total_fire_count !== undefined ? rule.total_fire_count : '—'}
                      </td>
                    )}

                    {visibleColumns.acknowledged_count && (
                      <td className="px-3.5 py-2.5 text-xs text-center text-gray-200 font-medium select-text">
                        {rule.acknowledged_count !== undefined ? rule.acknowledged_count : '—'}
                      </td>
                    )}

                    {visibleColumns.resolved_count && (
                      <td className="px-3.5 py-2.5 text-xs text-center text-gray-200 font-medium select-text">
                        {rule.resolved_count !== undefined ? rule.resolved_count : '—'}
                      </td>
                    )}

                    {visibleColumns.last_triggered_at && (
                      <td className="px-3.5 py-2.5 text-xs text-gray-200 font-medium select-text">
                        {rule.last_triggered_at !== undefined ? formatRelativeTime(rule.last_triggered_at) : '—'}
                      </td>
                    )}

                    {/* Actions */}
                    <td className="sticky right-0 bg-gray-900/95 backdrop-blur-md px-3.5 py-2.5 text-center shadow-[-8px_0_12px_-4px_rgba(0,0,0,0.5)] z-20">
                      <div className="relative inline-block text-left">
                        <button
                          onClick={(e) => handleActionMenuToggle(e, rule.id)}
                          className={`action-menu-trigger rounded-lg hover:border-gray-500 hover:bg-gray-700 p-1.5  flex items-center justify-center transition-all cursor-pointer ${activeActionMenuId === rule.id
                            ? 'border-blue-500  text-blue-400 ring-2 ring-blue-500/30'
                            : ' text-gray-200  hover:text-white'
                            }`}
                          title="Actions"
                        >
                          <CircleEllipsis className="w-4 h-4 " />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-3 py-2.5 bg-gray-900/80 border border-gray-800 rounded-xl text-xs text-gray-200">
        <div className="flex items-center gap-3">
          <span>
            Showing <span className="font-semibold text-white">{filteredRules.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}</span> to{' '}
            <span className="font-semibold text-white">{Math.min(currentPage * itemsPerPage, filteredRules.length)}</span> of{' '}
            <span className="font-semibold text-white">{filteredRules.length}</span> entries
          </span>
          <div className="flex items-center gap-2 border-l border-gray-800 pl-3">
            <span className="text-[11px] text-gray-200">Per page:</span>
            <ItemsPerPageDropdown
              value={itemsPerPage}
              onChange={(val) => {
                setItemsPerPage(val);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="p-1 rounded hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-200"
            title="First Page"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-1 rounded hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-200"
            title="Previous Page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <span className="px-2 py-0.5 text-xs text-gray-200 font-medium">
            Page <span className="text-white font-bold">{currentPage}</span> of <span className="text-white font-bold">{totalPages || 1}</span>
          </span>

          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages || totalPages === 0}
            className="p-1 rounded hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-200"
            title="Next Page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
            className="p-1 rounded hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-200"
            title="Last Page"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <CustomConfirm
        isOpen={deleteConfirmId !== null}
        title="Delete Automation Rule"
        message="Are you sure you want to delete this rule? This action cannot be undone."
        onConfirm={() => {
          if (deleteConfirmId !== null) {
            handleDelete(deleteConfirmId);
            setDeleteConfirmId(null);
          }
        }}
        onCancel={() => setDeleteConfirmId(null)}
      />

      <CustomConfirm
        isOpen={toggleConfirmRule !== null}
        title={toggleConfirmRule?.is_active ? "Pause Automation Rule?" : "Activate Automation Rule?"}
        message={`Are you sure you want to ${toggleConfirmRule?.is_active ? "pause" : "activate"} "${toggleConfirmRule?.name}"?`}
        confirmLabel={toggleConfirmRule?.is_active ? "Pause" : "Activate"}
        confirmColor={toggleConfirmRule?.is_active ? "orange" : "green"}
        onConfirm={() => {
          if (toggleConfirmRule !== null) {
            handleToggleActive(toggleConfirmRule.id);
            setToggleConfirmRule(null);
          }
        }}
        onCancel={() => setToggleConfirmRule(null)}
      />

      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!newRuleName.trim() || !newAssetType) {
                toast.error('Rule name and target asset type are required');
                return;
              }
              setIsCreateModalOpen(false);
              navigate('/automation/editor', {
                state: {
                  ruleName: newRuleName.trim(),
                  ruleDescription: newRuleDescription.trim(),
                  assetType: newAssetType
                }
              });
            }}
            className="bg-gray-900 border border-gray-800 rounded-xl p-5 w-96 max-w-full shadow-2xl space-y-4 animate-in fade-in zoom-in-95 duration-150"
          >
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <h3 className="text-sm font-bold text-white uppercase tracking-wider">Create Automation Rule</h3>
                <p className="text-[10px] text-gray-200">Enter a rule name, target asset type, and optional description.</p>
              </div>
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className="p-1 text-gray-200 hover:text-white rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-gray-200 uppercase tracking-wider block">
                Rule Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newRuleName}
                onChange={(e) => setNewRuleName(e.target.value)}
                placeholder="e.g. High CPU Load Notification, Port Status Monitor"
                autoFocus
                className="w-full bg-gray-950 border border-gray-800 focus:border-orange-500  /30 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-gray-200 uppercase tracking-wider block">
                Target Asset Type <span className="text-red-500">*</span>
              </label>
              <CustomSelect
                value={newAssetType}
                onChange={(val) => setNewAssetType(val)}
                options={assetTypeOptions}
                placeholder="Select Target Asset Type..."
                className="w-full font-sans"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-gray-200 uppercase tracking-wider block">Description</label>
              <textarea
                value={newRuleDescription}
                onChange={(e) => setNewRuleDescription(e.target.value)}
                placeholder="Brief description of what this automation rule does..."
                rows={3}
                className="w-full bg-gray-950 border border-gray-800 focus:border-orange-500  /30 rounded-lg px-3 py-2 text-xs text-white focus:outline-none resize-none"
              />
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-1">
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className="px-3 py-1.5 bg-gray-950 border border-gray-800 hover:bg-gray-800 text-gray-200 hover:text-white text-[11px] font-bold rounded-lg transition-colors cursor-pointer"
              >
                CANCEL
              </button>
              <button
                type="submit"
                disabled={!newRuleName.trim() || !newAssetType}
                className="px-3.5 py-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-[11px] font-bold rounded-lg transition-all shadow-md hover:shadow-orange-500/10 cursor-pointer"
              >
                CONTINUE
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Actions Portal Menu */}
      {activeActionMenuId && (() => {
        const activeRule = rules.find(r => r.id === activeActionMenuId);
        if (!activeRule) return null;
        return createPortal(
          <>
            <div
              className="fixed inset-0 z-[9998] cursor-default"
              onClick={(e) => {
                e.stopPropagation();
                setActiveActionMenuId(null);
              }}
            />
            <div
              className="fixed w-44 bg-[#0d1527] border border-gray-800/90 rounded-xl shadow-2xl overflow-hidden py-1 z-[9999] text-left action-menu-portal"
              style={{
                top: actionMenuPosition.top,
                left: actionMenuPosition.left,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveActionMenuId(null);
                  navigate(`/automation/editor/${activeRule.id}?mode=view`);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-gray-200 hover:text-white hover:bg-gray-800/70 transition-colors cursor-pointer"
              >
                <Eye className="w-4 h-4 text-cyan-400" />
                <span>View Details</span>
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveActionMenuId(null);
                  navigate(`/automation/editor/${activeRule.id}`);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-gray-200 hover:text-white hover:bg-gray-800/70 transition-colors cursor-pointer"
              >
                <Edit className="w-4 h-4 text-blue-400" />
                <span>Edit Rule</span>
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveActionMenuId(null);
                  setToggleConfirmRule(activeRule);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-gray-200 hover:text-white hover:bg-gray-800/70 transition-colors cursor-pointer"
              >
                <ToggleLeft className={`w-4 h-4 ${activeRule.is_active ? 'text-amber-400' : 'text-emerald-400'}`} />
                <span>{activeRule.is_active ? 'Pause Rule' : 'Activate Rule'}</span>
              </button>

              {!(activeRule.is_global && isClientLevel) && (
                <>
                  <div className="my-1 border-t border-gray-800/80" />

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (activeRule.is_default) return;
                      setActiveActionMenuId(null);
                      setDeleteConfirmId(activeRule.id);
                    }}
                    disabled={activeRule.is_default}
                    title={activeRule.is_default ? 'Default rules cannot be deleted' : undefined}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-colors ${activeRule.is_default
                      ? 'text-gray-500 opacity-50 cursor-not-allowed'
                      : 'text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer'
                      }`}
                  >
                    <Trash2 className={`w-4 h-4 ${activeRule.is_default ? 'text-gray-500' : 'text-red-400'}`} />
                    <span>Delete Rule</span>
                  </button>
                </>
              )}
            </div>
          </>,
          document.body
        );
      })()}

      {/* Columns Portal Menu */}
      {colMenuOpen && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998] cursor-default"
            onClick={(e) => {
              e.stopPropagation();
              setColMenuOpen(false);
            }}
          />
          <div
            className="fixed w-52 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-[9999] p-1.5 max-h-[450px] overflow-y-auto column-menu-portal"
            style={{
              top: colMenuPosition.top,
              left: colMenuPosition.left,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-1.5 text-[9px] font-bold text-gray-200 uppercase border-b border-gray-800 mb-1">
              Toggle Columns
            </div>
            {[
              { id: 'name', label: 'Rule Name', icon: FileText },
              { id: 'trigger', label: 'Trigger', icon: Zap },
              { id: 'actions', label: 'Actions', icon: Workflow },
              { id: 'is_global', label: 'Global', icon: Globe },
              { id: 'status', label: 'Status', icon: ToggleLeft },
              { id: 'severity', label: 'Severity', icon: AlertTriangle },
              { id: 'open_count', label: 'Open', icon: Bell },
              { id: 'total_fire_count', label: 'Fired', icon: ShieldQuestionMark },
              { id: 'acknowledged_count', label: 'Acknowledged', icon: ShieldCheck },
              { id: 'resolved_count', label: 'Resolved', icon: CheckCircle2 },
              { id: 'last_triggered_at', label: 'Last Fired', icon: Clock },
            ].map(col => (
              <button
                key={col.id}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleColumn(col.id);
                }}
                className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase text-gray-200 hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2.5">
                  <col.icon className={`w-3 h-3 ${visibleColumns[col.id] ? 'text-orange-500' : 'text-gray-200'}`} />
                  {col.label}
                </div>
                <div className={`w-3 h-3 rounded border flex items-center justify-center transition-colors ${visibleColumns[col.id] ? 'bg-orange-600 border-orange-600' : 'border-gray-700'}`}>
                  {visibleColumns[col.id] && <Check className="w-2.5 h-2.5 text-white" />}
                </div>
              </button>
            ))}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};
